/**
 * Format a UTC/ISO timestamp as "YYYY-MM-DD HH:MM" in the viewer's LOCAL time.
 *
 * Slicing the raw ISO string (`ts.slice(0, 16)`) shows UTC, so message/draft
 * times read hours off depending on the reader's zone. Going through `Date`
 * and the local getters fixes that while keeping the compact format.
 */
/**
 * Mail-client style timestamp (Apple Mail / Outlook): time of day for today,
 * "Yesterday", the weekday within the last week, then a short date — with the
 * year only when it isn't the current one.
 */
export function formatMailboxDate(ts: string | null | undefined): string {
    if (!ts) return "";
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return ts.slice(0, 10);
    const now = new Date();
    const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
    const days = Math.round((startOfDay(now) - startOfDay(d)) / 86_400_000);
    if (days <= 0) return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    if (days === 1) return "Yesterday";
    if (days < 7) return d.toLocaleDateString(undefined, { weekday: "long" });
    return d.toLocaleDateString(undefined, {
        day: "numeric",
        month: "short",
        year: d.getFullYear() === now.getFullYear() ? undefined : "numeric",
    });
}

/** Friendly full timestamp for expanded message headers:
 *  "Wed, 16 Jul 2026, 12:34 PM" in the viewer's locale. */
export function formatFullDateTime(ts: string | null | undefined): string {
    if (!ts) return "";
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return ts.slice(0, 16).replace("T", " ");
    return d.toLocaleString(undefined, {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
}

export function formatLocalDateTime(ts: string | null | undefined): string {
    if (!ts) return "";
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return ts.slice(0, 16).replace("T", " ");
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
